// src/pages/Exams/generate/GenerationProgress.tsx
import { useState, type ReactNode } from 'react';
import { Badge, Button, PanelCard, TextLink, Track } from '../../../school_panel/components/ui';
import type { JobRow } from './types';
import { timeOf } from '../../../school_panel/lib/dates';

interface Props {
  job: JobRow;
  onCancel: () => Promise<void>;
  /** Failed phase only: resets the blueprint and drops the job (page-owned). */
  onStartOver?: () => void;
  /** Rendered at the foot of the finished card (the coverage report). */
  children?: ReactNode;
}

const PHASE_COPY: Record<string, string> = {
  queued: 'Queued',
  fetching_chunks: 'Fetching source material',
  generating: 'Generating questions',
  scoring: 'Scoring quality',
  cancelled: 'Cancelled',
};

// The phases a job walks through, in order. review_pending is the last stop, so
// it reads as done once the job gets there (cancelled/failed mark nothing).
const PHASE_ORDER = ['queued', 'fetching_chunks', 'generating', 'scoring', 'review_pending'] as const;
const PHASE_LABEL: Record<string, string> = { ...PHASE_COPY, review_pending: 'Ready for review' };

const REASON_COPY: Record<string, string> = {
  no_source: 'no textbook content linked',
  gemini_error: 'AI call failed',
  timeout: 'AI call timed out',
  parse_failed: 'AI returned unparseable output',
  ai_limit: 'School AI credit ended',
  openrouter_invalid_key: 'Your OpenRouter key was refused',
  openrouter_daily_limit: "Your OpenRouter key hit today's free limit",
  openrouter_busy: 'Free AI models were busy',
};

/** Failure reasons the person can fix on My AI (buy credit or add/replace a key). */
const AI_CREDIT_REASONS = new Set(['ai_limit', 'openrouter_invalid_key', 'openrouter_daily_limit', 'openrouter_busy']);

function MyAiLink() {
  return (
    <TextLink to="/settings/ai" className="text-ink underline underline-offset-[3px]">Get more AI credit</TextLink>
  );
}

/** "08:41:02" from an ISO timestamp; '' when it is missing or invalid. */
function clock(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return timeOf(d, { seconds: true });
}

/** "54 s" / "2 min 5 s" between two ISO timestamps; null when either is missing. */
export function durationLabel(start?: string | null, end?: string | null): string | null {
  if (!start || !end) return null;
  const s = new Date(start).getTime();
  const e = new Date(end).getTime();
  if (Number.isNaN(s) || Number.isNaN(e) || e < s) return null;
  const secs = Math.round((e - s) / 1000);
  return secs >= 120 ? `${Math.floor(secs / 60)} min ${secs % 60} s` : `${secs} s`;
}

function PhaseList({ job }: { job: JobRow }) {
  const phase = job.phase;
  const at = PHASE_ORDER.indexOf(phase as typeof PHASE_ORDER[number]);
  const finished = phase === 'review_pending' || phase === 'saved';
  const reached = finished ? PHASE_ORDER.length - 1 : at;
  const failedChapters = (job.failed_chapters ?? []).length;
  const flagged = (job.output_questions ?? []).filter(q => (q.flags ?? []).length > 0).length;
  const subOf = (p: typeof PHASE_ORDER[number], state: string): string => {
    if (p === 'queued') return `Job #${job.id} accepted`;
    if (p === 'generating' && state !== 'upcoming') {
      return `${job.progress_completed} done${failedChapters > 0 ? ` · ${failedChapters} chapter${failedChapters === 1 ? '' : 's'} failed` : ''}`;
    }
    if (p === 'scoring' && state === 'done') return `${flagged} question${flagged === 1 ? '' : 's'} flagged`;
    if (p === 'review_pending') return 'Review, edit or remove below';
    return '';
  };
  const timeOf = (p: typeof PHASE_ORDER[number], state: string): string => {
    if (state === 'current') return 'now';
    if (p === 'queued') return clock(job.created_at);
    return '';
  };
  return (
    <ol aria-label="Generation steps" className="m-0 grid list-none grid-cols-1 gap-2 p-0 min-[821px]:grid-cols-3 min-[1181px]:grid-cols-5">
      {PHASE_ORDER.map((p, i) => {
        // The last phase is "now" once the job is waiting for review, like the design.
        const state = i < reached ? 'done' : i === reached ? 'current' : 'upcoming';
        const tile = state === 'done' ? 'bg-success-soft text-ink' : state === 'current' ? 'bg-navy text-white' : 'bg-panel text-ink';
        const dot = state === 'done' ? 'bg-success-bg text-success' : state === 'current' ? 'bg-accent text-navy' : 'bg-track text-muted-2';
        const muted = state === 'current' ? 'text-on-navy-muted' : 'text-muted';
        const sub = subOf(p, state);
        const time = timeOf(p, state);
        return (
          <li key={p} data-state={state}
            aria-current={state === 'current' ? 'step' : undefined}
            className={`flex min-w-0 flex-col gap-1.5 rounded-[14px] px-3.5 py-3 ${tile}`}>
            <span className="flex items-center justify-between gap-2">
              <span aria-hidden className={`grid h-6 w-6 place-items-center rounded-full font-mono text-xs font-semibold ${dot}`}>
                {state === 'done' ? '✓' : i + 1}
              </span>
              {time && <span className={`font-mono text-xs ${muted}`}>{time}</span>}
            </span>
            <span className="font-semibold">{PHASE_LABEL[p]}</span>
            {sub && <span className={`text-xs ${muted}`}>{sub}</span>}
          </li>
        );
      })}
    </ol>
  );
}

export function GenerationProgress({ job, onCancel, onStartOver, children }: Props) {
  const [showFailed, setShowFailed] = useState(false);
  const total = Math.max(1, job.progress_total);
  const completed = job.progress_completed;
  const failed = job.progress_failed;
  const pct = Math.min(100, Math.round(((completed + failed) / total) * 100));
  const failedChapters = job.failed_chapters ?? [];
  // Only claim "no textbook content" when that is in fact every failure; a
  // timeout or a parse error gets the neutral wording instead.
  const allNoSource = failedChapters.length > 0
    && failedChapters.every(f => f.reason === 'no_source');
  const aiCreditFailed = failedChapters.some(f => AI_CREDIT_REASONS.has(f.reason));

  if (job.phase === 'failed') {
    return (
      <div className="rounded-[20px] border border-danger/40 bg-danger-bg px-4 py-6 text-center">
        <h4 className="font-display text-base font-bold text-danger-ink">Generation failed</h4>
        <p className="mx-auto mt-1 max-w-md text-sm text-ink">
          {job.error_message || 'Something went wrong. Try again.'}
        </p>
        {onStartOver && (
          <div className="mt-3 flex justify-center">
            <Button variant="ghost" onClick={onStartOver}>Start over</Button>
          </div>
        )}
      </div>
    );
  }

  const finished = job.phase === 'review_pending' || job.phase === 'saved';
  const delivered = (job.output_questions ?? []).length;
  const forms = Math.max(1, job.blueprint?.num_forms ?? 1);
  const planned = (job.blueprint?.chapter_distribution ?? []).reduce((s, e) => s + e.count, 0) * forms;
  const needAttention = new Set(failedChapters.map(f => f.chapter_id)).size;
  const took = durationLabel(job.started_at, job.completed_at);
  const subtitle = finished
    ? `${planned > 0 ? `${delivered} of ${planned}` : delivered} questions generated${took ? ` in ${took}` : ''}${needAttention > 0 ? ` · ${needAttention} chapter${needAttention === 1 ? '' : 's'} need${needAttention === 1 ? 's' : ''} attention` : ''}`
    : <span className="font-mono">
        {failed > 0 ? `${completed} done · ${failed} failed of ${total}` : `${completed} / ${total}`} ({pct}%)
      </span>;

  return (
    <PanelCard
      title={finished ? 'Generation finished' : (PHASE_COPY[job.phase] ?? job.phase)}
      subtitle={subtitle}
      actions={finished ? <Badge tone="accent">Ready for review</Badge> : job.phase === 'cancelled' ? <Badge tone="neutral">Cancelled</Badge> : undefined}
    >
      <div className="space-y-4">
        <Track value={finished ? 100 : pct} label="Generation progress" className="!h-2.5" />

        <PhaseList job={job} />

        {!finished && <p className="text-sm text-muted">{job.current_label || 'Working…'}</p>}

        {failed > 0 && !finished && (
          <div className="rounded-[12px] border border-warning/40 bg-warning-bg px-3.5 py-2.5 text-sm text-warning-ink">
            <button
              type="button"
              aria-expanded={showFailed}
              onClick={() => setShowFailed(s => !s)}
              className="min-h-[36px] font-semibold hover:underline">
              {failed} chapter{failed === 1 ? '' : 's'}{' '}
              {allNoSource ? 'had no textbook content' : 'failed'} — view details
            </button>
            {showFailed && (
              <ul className="mt-1 space-y-0.5 pl-4">
                {failedChapters.map((f, i) => (
                  <li key={i}>
                    Form {f.form_label} — {f.chapter_name}: {REASON_COPY[f.reason] ?? f.reason}
                  </li>
                ))}
              </ul>
            )}
            {aiCreditFailed && <div><MyAiLink /></div>}
          </div>
        )}

        {finished && aiCreditFailed && (
          <p className="flex flex-wrap items-center gap-x-2 text-sm text-muted">
            <span>Some chapters stopped: AI credit ran out or your OpenRouter key failed.</span>
            <MyAiLink />
          </p>
        )}

        {job.phase === 'cancelled' ? (
          <p className="text-sm text-muted">
            Generation cancelled. Partial output is preserved below.
          </p>
        ) : !finished ? (
          <div className="flex justify-end">
            <Button variant="ghost" onClick={onCancel}>Cancel</Button>
          </div>
        ) : null}

        {children}
      </div>
    </PanelCard>
  );
}
